<template>
  <main class="catalog-page" :class="{ 'has-context': Boolean(institution) }">
    <header class="catalog-heading">
      <p v-if="!institution" class="eyebrow">
        {{ ui("DITT NESTE SEMESTER", "YOUR NEXT SEMESTER") }}
      </p>
      <h1>
        {{
          ranking
            ? ui(
                "Enkleste og vanskeligste emner.",
                "Easiest and hardest courses."
              )
            : ui("Finn fag som passer deg.", "Find courses that fit you.")
        }}
      </h1>
      <p>
        {{
          ui(
            ranking
              ? "Utforsk historiske resultater. Velg hva du vil rangere etter, og sammenlign emnene på samme grunnlag."
              : "Utforsk emner ved studiestedet ditt og samle en kortliste til sammenligning.",
            ranking
              ? "Explore historical results. Choose a ranking and compare courses on the same basis."
              : "Explore courses at your institution and build a shortlist to compare."
          )
        }}
      </p>
    </header>

    <button
      v-if="institution"
      type="button"
      class="context-summary"
      :aria-expanded="contextExpanded"
      aria-controls="catalog-context-fields"
      @click="contextExpanded = !contextExpanded"
    >
      <span
        ><strong>{{ contextInstitutionName }}</strong
        ><span>{{ contextProgramName }}</span></span
      ><span
        >{{ contextExpanded ? ui("Lukk", "Close") : ui("Endre", "Change") }}
        <v-icon size="16" aria-hidden="true">{{
          contextExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
        }}</v-icon></span
      >
    </button>
    <section
      id="catalog-context-fields"
      class="context-filters"
      :class="{ 'context-collapsed': institution && !contextExpanded }"
      :aria-label="
        ui('Studiested og studieprogram', 'Institution and programme')
      "
    >
      <label
        >{{ ui("Studiested", "Institution") }}
        <select
          v-model="institution"
          :disabled="loadingInstitutions"
          @change="changeInstitution"
        >
          <option value="">
            {{
              loadingInstitutions
                ? ui("Henter studiesteder …", "Loading institutions …")
                : ui("Velg studiested", "Choose institution")
            }}
          </option>
          <option
            v-for="item in institutions"
            :key="item.code"
            :value="item.code"
          >
            {{ item.name }}
          </option>
        </select>
      </label>
      <label
        >{{ ui("Studieprogram", "Study programme") }}
        <select
          v-model="program"
          :disabled="!institution || loadingPrograms"
          @change="changeFilters"
        >
          <option value="">
            {{
              loadingPrograms
                ? ui("Henter studieprogram …", "Loading programmes …")
                : ui("Alle studieprogram", "All programmes")
            }}
          </option>
          <option v-for="item in programs" :key="item.code" :value="item.code">
            {{ item.name }}
          </option>
        </select>
      </label>
    </section>
    <p v-if="programError" class="catalog-error" role="alert">
      {{ programError }}
      <button type="button" @click="loadPrograms">
        {{ ui("Prøv igjen", "Retry") }}
      </button>
    </p>

    <template v-if="institution">
      <nav
        v-if="ranking"
        class="ranking-presets"
        :aria-label="ui('Ranger etter', 'Rank by')"
      >
        <button
          v-for="preset in rankingPresets"
          :key="preset.value"
          type="button"
          :aria-pressed="sort === preset.value"
          @click="
            sort = preset.value;
            changeFilters();
          "
        >
          {{ preset.label }}
        </button>
      </nav>
      <div class="catalog-tools" :class="{ 'ranking-tools': ranking }">
        <label class="catalog-search"
          ><v-icon size="19" aria-hidden="true">mdi-magnify</v-icon
          ><input
            ref="courseSearch"
            v-model="search"
            type="search"
            :aria-label="ui('Søk etter emne', 'Search courses')"
            :placeholder="
              ui(
                'Emnenavn eller kode',
                'Course name or code'
              )
            "
            @input="queueSearch"
            @keydown.esc="clearSearch"
          /><button
            v-if="search"
            type="button"
            class="clear-search"
            :aria-label="ui('Tøm søket', 'Clear search')"
            @click="clearSearch"
          >
            ×
          </button></label
        >
        <label
          >{{ ui("Historisk semester", "Historical semester")
          }}<select v-model="semester" @change="changeFilters">
            <option value="">{{ ui("Alle", "All") }}</option>
            <option value="1">{{ ui("Vår", "Spring") }}</option>
            <option value="3">{{ ui("Høst", "Autumn") }}</option>
          </select></label
        >
        <label v-if="!ranking"
          >{{ ui("Sorter etter", "Sort by")
          }}<select v-model="sort" @change="changeFilters">
            <option value="code">{{ ui("Emnekode", "Course code") }}</option>
            <option value="grade_desc">
              {{ ui("Høyest snitt", "Highest average") }}
            </option>
            <option value="grade_asc">
              {{ ui("Lavest snitt", "Lowest average") }}
            </option>
            <option value="fail_asc">
              {{ ui("Lavest stryk", "Lowest failure rate") }}
            </option>
            <option value="fail_desc">
              {{ ui("Høyest stryk", "Highest failure rate") }}
            </option>
          </select></label
        >
      </div>

      <div
        v-if="activeFilters.length"
        class="active-filters"
        :aria-label="ui('Aktive filtre', 'Active filters')"
      >
        <button
          v-for="filter in activeFilters"
          :key="filter.key"
          type="button"
          :class="{ 'program-filter-chip': filter.key === 'program' }"
          :aria-label="`${ui('Fjern filter', 'Remove filter')}: ${
            filter.label
          }`"
          @click="clearFilter(filter.key)"
        >
          <span>{{ filter.label }}</span
          ><span
            v-if="filter.key === 'program'"
            class="program-code"
            aria-hidden="true"
            >{{ program }}</span
          ><span aria-hidden="true">×</span
          ><span class="sr-only">{{
            ui("Fjern filter", "Remove filter")
          }}</span>
        </button>
        <button
          v-if="activeFilters.length > 1"
          type="button"
          class="reset-filters"
          @click="resetFilters"
        >
          {{ ui("Nullstill filtre", "Reset filters") }}
        </button>
      </div>
      <div v-if="error" class="catalog-error" role="alert">
        {{ error }}
        <button type="button" @click="loadCourses">
          {{ ui("Prøv igjen", "Retry") }}
        </button>
      </div>
      <div v-if="loading && !payload" class="catalog-loading" role="status">
        {{
          ui(
            "Henter emner og datagrunnlag …",
            "Loading courses and their data …"
          )
        }}
      </div>

      <template v-if="payload && !error">
        <div ref="listContext" class="list-context" role="status" tabindex="-1">
          <strong>{{
            loading ? ui("Oppdaterer …", "Updating …") : resultCountLabel
          }}</strong
          ><span
            >{{ ui("Snitt og stryk", "Average and failure rate") }} ·
            {{ payload.first_year || "—" }}–{{ payload.last_year || "—" }} ·
            {{ ui("F inkludert", "F included") }}</span
          >
        </div>
        <p class="catalog-data-note">
          {{
            ui(
              "Semester viser historikk. Studiepoeng mangler foreløpig i kilden.",
              "Semesters show historical records. Credits are not yet available from the source."
            )
          }}
        </p>
        <div
          class="catalog-table-wrap"
          :class="{ 'is-updating': loading }"
          :aria-busy="loading"
        >
          <table
            v-if="courses.length"
            class="catalog-table"
            :class="{ 'without-credits': !hasCredits }"
          >
            <caption class="sr-only">
              {{
                ui(
                  "Emner med historisk karakterstatistikk",
                  "Courses with historical grade statistics"
                )
              }}
            </caption>
            <thead>
              <tr>
                <th scope="col">{{ ui("Emne", "Course") }}</th>
                <th v-if="hasCredits" scope="col">
                  {{ ui("Studiepoeng", "Credits") }}
                </th>
                <th scope="col">{{ ui("Semester", "Semester") }}</th>
                <th scope="col" class="numeric">
                  {{ ui("Snitt", "Average") }}
                </th>
                <th scope="col" class="numeric">
                  {{ ui("Stryk", "Failure") }}
                </th>
                <th scope="col">
                  <span class="sr-only">{{
                    ui("Velg til kortlisten", "Add to shortlist")
                  }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="course in courses"
                :key="course.course_code"
                :class="{ 'is-selected': isSelected(course) }"
              >
                <td class="course-identity">
                  <span class="course-code">{{ course.course_code }}</span
                  ><router-link :to="courseRoute(course)">{{
                    course.course_name
                  }}</router-link>
                  <details class="course-basis">
                    <summary>
                      {{
                        course.data_issue
                          ? ui(
                              "Datagrunnlag må avklares",
                              "Data needs clarification"
                            )
                          : `${coursePeriod(course)} · ${formatNumber(
                              course.sample_count
                            )} ${ui("resultater", "results")}`
                      }}
                    </summary>
                    <p v-if="course.data_issue">
                      {{ dataIssue(course.data_issue) }}
                    </p>
                    <p v-else>
                      {{
                        ui(
                          "Snitt beregnes fra A–F-fordelingen, vektet etter antall resultater. Strykandelen bruker alle registrerte resultater, også bestått/ikke bestått. Tallene gjelder hele emnet, ikke bare valgt studieprogram.",
                          "The average is weighted by A–F result counts. Failure rates include all recorded results, including pass/fail. Figures describe the whole course, not just the selected programme."
                        )
                      }}
                      {{ ui("A = 5, F = 0.", "A = 5, F = 0.") }}<br />{{
                        ui("A–F-resultater", "A–F results")
                      }}: {{ formatNumber(course.average_count) }}.
                      {{
                        ui(
                          "Registrerte resultater er ikke nødvendigvis unike studenter.",
                          "Recorded results are not necessarily unique students."
                        )
                      }}
                    </p>
                  </details>
                  <SampleSizeBadge
                    v-if="sampleCount(course) > 0 && sampleCount(course) < 50"
                    :count="sampleCount(course)"
                    :locale="state.locale"
                  />
                </td>
                <td
                  v-if="hasCredits"
                  :data-label="ui('Studiepoeng', 'Credits')"
                >
                  <span v-if="course.credits != null">{{
                    formatNumber(course.credits)
                  }}</span
                  ><span
                    v-else
                    class="missing-credit"
                    :aria-label="
                      ui(
                        'Studiepoeng ikke oppgitt i datakilden',
                        'Credits not supplied by the data source'
                      )
                    "
                    >—</span
                  >
                </td>
                <td :data-label="ui('Semester', 'Semester')">
                  {{ semesterLabel(course.historical_semesters) }}
                </td>
                <td :data-label="ui('Snitt', 'Average')" class="numeric">
                  <strong class="grade-letter">{{
                    gradeLabel(course.average_grade)
                  }}</strong
                  ><span
                    v-if="course.average_grade != null"
                    class="grade-number"
                    >{{ formatNumber(course.average_grade, 2) }}</span
                  ><span
                    v-else-if="course.sample_count > 0 && !course.data_issue"
                    class="missing-credit"
                    >{{ ui("B/IB", "Pass/fail") }}</span
                  >
                </td>
                <td :data-label="ui('Stryk', 'Failure')" class="numeric">
                  {{
                    course.failure_rate == null
                      ? "—"
                      : `${formatNumber(course.failure_rate * 100, 1)} %`
                  }}
                </td>
                <td class="row-action">
                  <button
                    type="button"
                    class="add-shortlist"
                    :aria-pressed="isSelected(course)"
                    :aria-label="`${
                      isSelected(course)
                        ? ui('Fjern', 'Remove')
                        : ui('Velg', 'Select')
                    } ${course.course_name}`"
                    :disabled="
                      loading ||
                      Boolean(course.data_issue) ||
                      (!isSelected(course) && shortlist.length >= compareLimit)
                    "
                    :aria-describedby="
                      !isSelected(course) && shortlist.length >= compareLimit
                        ? 'shortlist-hint'
                        : undefined
                    "
                    @click="toggleCourse(course)"
                  >
                    {{
                      isSelected(course)
                        ? ui("✓ Valgt", "✓ Selected")
                        : ui("+ Velg", "+ Select")
                    }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-results">
            <h2>
              {{
                ui(
                  "Ingen emner passer utvalget.",
                  "No courses match these filters."
                )
              }}
            </h2>
            <p>
              {{
                ui(
                  "Prøv et annet søk, semester eller studieprogram.",
                  "Try another search, semester or programme."
                )
              }}
            </p>
            <button type="button" @click="resetSearch">
              {{ ui("Nullstill søk og semester", "Reset search and semester") }}
            </button>
          </div>
        </div>

        <nav
          v-if="payload.total > payload.page_size"
          class="catalog-pagination"
          :aria-label="ui('Sider med emner', 'Course pages')"
        >
          <button
            type="button"
            :disabled="loading || page <= 1"
            @click="goToPage(page - 1)"
          >
            ← {{ ui("Forrige", "Previous") }}</button
          ><span aria-live="polite"
            >{{ formatNumber((payload.page - 1) * payload.page_size + 1) }}–{{
              formatNumber(
                Math.min(payload.page * payload.page_size, payload.total)
              )
            }}
            {{ ui("av", "of") }} {{ formatNumber(payload.total) }}</span
          ><button
            type="button"
            :disabled="
              loading || page >= Math.ceil(payload.total / payload.page_size)
            "
            @click="goToPage(page + 1)"
          >
            {{ ui("Neste", "Next") }} →
          </button>
        </nav>
        <details class="catalog-method">
          <summary>
            {{
              ui(
                "Hva tallene forteller — og hva som mangler",
                "What the figures tell you — and what is missing"
              )
            }}
          </summary>
          <p>
            {{
              ui(
                "Semester viser hvor vi har registrert emnet i historiske data",
                "Semester shows historical records of a course"
              )
            }}
            ({{ payload.offering_first_year }}–{{
              payload.offering_last_year
            }}).
            {{
              ui(
                "Det bekrefter ikke framtidig undervisning eller at emnet kan godkjennes som valgfag. Studiepoeng er foreløpig ikke oppgitt i datakilden vår. Høyt snitt er ikke alene et mål på lav vanskelighetsgrad.",
                "This does not confirm future teaching or eligibility as an elective. Our source does not yet provide credits. A high average alone does not measure low difficulty."
              )
            }}
          </p>
        </details>
      </template>
    </template>
    <section v-else-if="!error" class="catalog-welcome">
      <span aria-hidden="true">↗</span>
      <h2>
        {{ ui("Start med studiestedet ditt.", "Start with your institution.") }}
      </h2>
      <p>
        {{
          ui(
            "Velg deretter studieprogram for å finne emner som er knyttet til programmet i datagrunnlaget.",
            "Then choose a programme to find courses linked to it in the source data."
          )
        }}
      </p>
      <div>
        <button
          v-for="item in featuredInstitutions"
          :key="item.code"
          type="button"
          @click="
            institution = item.code;
            changeInstitution();
          "
        >
          {{ institutionShortName(item) }} →
        </button>
      </div>
    </section>
    <div v-if="!institution && error" class="catalog-error" role="alert">
      {{ error }}
      <button type="button" @click="initialize">
        {{ ui("Prøv igjen", "Retry") }}
      </button>
    </div>

    <aside
      v-if="institution"
      class="shortlist-dock"
      :class="{
        'shortlist-collapsed': !shortlistExpanded,
        'shortlist-empty-state': !shortlist.length,
        'shortlist-full': shortlist.length >= compareLimit,
        'shortlist-ready': shortlist.length >= 2 && shortlist.length < compareLimit,
      }"
      :aria-label="ui('Kortlisten din', 'Your shortlist')"
    >
      <div class="shortlist-title">
        <h2 ref="shortlistHeading" tabindex="-1">
          {{ ui("Kortlisten din", "Your shortlist") }}
        </h2>
        <span>{{ shortlist.length }} / {{ compareLimit }}</span
        ><button
          v-if="shortlist.length"
          type="button"
          class="shortlist-disclosure"
          :aria-expanded="shortlistExpanded"
          aria-controls="shortlist-items"
          @click="shortlistExpanded = !shortlistExpanded"
        >
          {{
            shortlistExpanded
              ? ui("Skjul valgte", "Hide selection")
              : ui("Vis valgte", "Show selection")
          }}
          <v-icon size="16" aria-hidden="true">{{
            shortlistExpanded ? "mdi-chevron-down" : "mdi-chevron-up"
          }}</v-icon></button
        ><button
          v-if="shortlist.length"
          type="button"
          class="clear-shortlist"
          @click="clearShortlist"
        >
          {{ ui("Tøm", "Clear") }}
        </button>
      </div>
      <div
        v-show="shortlistExpanded || !shortlist.length"
        id="shortlist-items"
        class="shortlist-items"
        tabindex="0"
        :aria-label="ui('Valgte emner, rull sideveis ved behov', 'Selected courses, scroll sideways if needed')"
        :aria-describedby="shortlist.length > 1 ? 'shortlist-scroll-hint' : undefined"
      >
        <div
          v-for="course in shortlist"
          :key="course.course_code"
          class="shortlist-item"
        >
          <div>
            <span>{{ course.course_code }}</span
            ><strong>{{ course.course_name }}</strong>
          </div>
          <button
            type="button"
            :aria-label="`${ui('Fjern', 'Remove')} ${course.course_name}`"
            @click="toggleCourse(course)"
          >
            ×
          </button>
        </div>
        <p v-if="!shortlist.length" class="shortlist-empty">
          {{
            ui(
              "Velg emner fra listen. Kortlisten beholdes når du søker og filtrerer.",
              "Select courses from the list. Your shortlist stays while you search and filter."
            )
          }}
        </p>
      </div>
      <p v-if="shortlistExpanded && shortlist.length > 1" id="shortlist-scroll-hint" class="shortlist-scroll-hint">
        {{ ui('Sveip sideveis for å se alle fag →', 'Swipe sideways to see all courses →') }}
      </p>
      <div class="shortlist-actions">
        <button
          type="button"
          class="compare-action"
          :aria-label="compareActionLabel"
          :disabled="shortlist.length < 2"
          @click="compareCourses"
        >
          <span class="compare-action-full">{{ compareActionLabel }}</span>
          <span class="compare-action-compact" aria-hidden="true"
            >{{ ui("Sammenlign", "Compare") }} {{ shortlist.length }} →</span
          ></button
        ><span id="shortlist-hint">{{
          shortlist.length < 2
            ? ui("Velg minst 2 emner", "Choose at least 2 courses")
            : shortlist.length >= compareLimit
            ? ui(
                "Listen er full. Fjern et emne for å bytte.",
                "List full. Remove a course to swap it."
              )
            : ui("Åpne i felles graf", "Open shared chart")
        }}</span>
      </div>
    </aside>
    <p class="sr-only" role="status" aria-live="polite">{{ feedback }}</p>
    <p class="catalog-source">
      HK-dir / DBH ·
      {{
        ui(
          "Historiske resultater, ikke en garanti for framtidige karakterer.",
          "Historical results, not a guarantee of future grades."
        )
      }}
      <router-link to="/about"
        >{{ ui("Om datagrunnlaget", "About the data") }} ↗</router-link
      >
    </p>
  </main>
</template>

<script>
import { inject, markRaw } from "vue";
import ApiService from "@/ApiService";
import SampleSizeBadge from "@/components/SampleSizeBadge.vue";
import { courseSelection } from "@/courseSelection";
const STORAGE_KEY = "onlygrades-course-shortlist-v1";
export default {
  name: "CourseCatalogPage",
  components: { SampleSizeBadge },
  props: { ranking: Boolean },
  setup() {
    return { state: inject("state"), selection: courseSelection.state };
  },
  data: () => ({
    institutions: [],
    programs: [],
    institution: "",
    program: "",
    semester: "",
    sort: "code",
    search: "",
    page: 1,
    courses: [],
    contextExpanded: !window.matchMedia("(max-width: 600px)").matches,
    contextReady: false,
    disposed: false,
    shortlistExpanded: !window.matchMedia("(max-width: 600px)").matches,
    payload: null,
    loadingInstitutions: false,
    loadingPrograms: false,
    loading: false,
    error: "",
    programError: "",
    feedback: "",
    requestVersion: 0,
    programVersion: 0,
    searchTimer: null,
    requestController: null,
  }),
  computed: {
    filterStorageKey() {
      return this.ranking
        ? "onlygrades-ranking-filters-v1"
        : "onlygrades-course-filters-v1";
    },
    shortlist: {
      get() {
        return this.selection.courses;
      },
      set(courses) {
        courseSelection.replaceCourses(courses);
      },
    },
    contextInstitutionName() {
      const item = this.institutions.find(
        (item) => item.code === this.institution
      );
      return item ? this.institutionShortName(item) : this.institution;
    },
    contextProgramName() {
      return (
        this.programs.find((item) => item.code === this.program)?.name ||
        this.program ||
        this.ui("Alle studieprogram", "All programmes")
      );
    },
    hasCredits() {
      return this.courses.some((course) => course.credits != null);
    },
    compareActionLabel() {
      const count = this.shortlist.length;
      return `${this.ui("Sammenlign", "Compare")} ${count ? `${count} ` : ""}${this.courseNoun(count)} →`;
    },
    rankingPresets() {
      return [
        {
          value: "grade_desc",
          label: this.ui("Høyest snitt", "Highest average"),
        },
        {
          value: "grade_asc",
          label: this.ui("Lavest snitt", "Lowest average"),
        },
        { value: "fail_asc", label: this.ui("Lavest stryk", "Lowest failure") },
        {
          value: "fail_desc",
          label: this.ui("Høyest stryk", "Highest failure"),
        },
      ];
    },
    resultCountLabel() {
      if (!this.payload) return "";
      const { total, page, page_size: size } = this.payload;
      const count = `${this.formatNumber(total)} ${this.courseNoun(total)}`;
      if (total <= size) return count;
      return `${this.formatNumber((page - 1) * size + 1)}–${this.formatNumber(
        Math.min(page * size, total)
      )} ${this.ui("av", "of")} ${count}`;
    },
    activeFilters() {
      const filters = [];
      if (this.program)
        filters.push({
          key: "program",
          label:
            this.programs.find((item) => item.code === this.program)?.name ||
            this.program,
        });
      if (this.semester)
        filters.push({
          key: "semester",
          label:
            this.semester === "1"
              ? this.ui("Vår", "Spring")
              : this.ui("Høst", "Autumn"),
        });
      if (this.search.trim())
        filters.push({ key: "search", label: `“${this.search.trim()}”` });
      return filters;
    },
    compareLimit() {
      const restricted =
        process.env.NODE_ENV === "production" &&
        this.state.features.premiumEnabled &&
        !this.state.entitlement?.is_paid;
      return restricted
        ? 2
        : this.state.features.premiumEnabled && this.state.entitlement?.is_paid
        ? 8
        : 4;
    },
    featuredInstitutions() {
      return ["1150", "1110", "1120", "1130"]
        .map((code) => this.institutions.find((item) => item.code === code))
        .filter(Boolean);
    },
  },
  watch: {
    "selection.revision"() {
      if (
        !this.contextReady ||
        (this.institution === this.selection.institution &&
          this.program === this.selection.program)
      )
        return;
      this.institution = this.selection.institution;
      this.program = this.selection.program;
      this.page = 1;
      this.payload = null;
      this.courses = [];
      this.requestVersion++;
      this.requestController?.abort();
      if (this.institution) {
        this.loadPrograms();
        this.loadCourses();
      }
    },
    compareLimit(value) {
      this.shortlist = this.shortlist.slice(0, value);
      this.persist();
    },
  },
  created() {
    this.initialize();
  },
  beforeUnmount() {
    this.disposed = true;
    this.contextReady = false;
    clearTimeout(this.searchTimer);
    this.requestVersion++;
    this.programVersion++;
    this.requestController?.abort();
  },
  methods: {
    courseNoun(count) {
      return count === 1
        ? this.ui("emne", "course")
        : this.ui("emner", "courses");
    },
    clearSearch() {
      if (!this.search) return;
      this.search = "";
      this.changeFilters();
      this.$refs.courseSearch?.focus();
    },
    async clearShortlist() {
      this.shortlist = [];
      this.feedback = this.ui(
        "Kortlisten er tømt.",
        "Your shortlist is cleared."
      );
      this.persist();
      await this.$nextTick();
      this.$refs.shortlistHeading?.focus({ preventScroll: true });
    },
    coursePeriod(course) {
      if (!course.first_year) return "—";
      return course.first_year === course.last_year
        ? String(course.first_year)
        : `${course.first_year}–${course.last_year}`;
    },
    courseRoute(course) {
      return {
        name: "homeWithCourse",
        params: { courseCode: course.course_code },
        query: {
          source: this.ranking ? "rankings" : "explore",
          institution: this.institution,
          ...(this.program ? { program: this.program } : {}),
        },
      };
    },
    clearFilter(key) {
      this[key] = "";
      this.changeFilters();
    },
    resetFilters() {
      this.program = "";
      this.search = "";
      this.semester = "";
      this.sort = this.ranking ? "grade_desc" : "code";
      this.changeFilters();
    },
    ui(no, en) {
      return this.state.locale === "no" ? no : en;
    },
    formatNumber(value, digits = 0) {
      return value == null
        ? "—"
        : new Intl.NumberFormat(
            this.state.locale === "no" ? "nb-NO" : "en-GB",
            {
              minimumFractionDigits: digits,
              maximumFractionDigits: Math.max(digits, 1),
            }
          ).format(value);
    },
    gradeLabel(value) {
      return value == null
        ? "—"
        : ["F", "E", "D", "C", "B", "A"][
            Math.max(0, Math.min(5, Math.round(value)))
          ];
    },
    semesterLabel(values = []) {
      return (
        values
          .map((value) =>
            value === 1 ? this.ui("Vår", "Spring") : this.ui("Høst", "Autumn")
          )
          .join(" · ") || "—"
      );
    },
    institutionShortName(item) {
      return (
        { 1150: "NTNU", 1110: "UiO", 1120: "UiB", 1130: "UiT" }[item.code] ||
        item.name
      );
    },
    sampleCount(course) {
      return course.average_count > 0
        ? Math.min(course.average_count, course.sample_count)
        : course.sample_count;
    },
    dataIssue(issue) {
      return issue === "ambiguous-institution"
        ? this.ui(
            "Emnekoden er knyttet til flere læresteder. Tall og sammenligning holdes tilbake til tilknytningen er avklart.",
            "This code is linked to multiple institutions. Statistics and comparison are withheld until the identity is clarified."
          )
        : this.ui(
            "Kildedataene inneholder overlappende perioder eller ufullstendige antall. Tall og sammenligning holdes tilbake til grunnlaget er avklart.",
            "The source contains overlapping periods or incomplete counts. Statistics and comparison are withheld until the data is clarified."
          );
    },
    async initialize() {
      this.loadingInstitutions = true;
      this.error = "";
      try {
        const response = await ApiService.getInstitutions();
        if (this.disposed) return;
        this.institutions = response.data;
        let saved = {};
        try {
          saved = JSON.parse(
            localStorage.getItem(this.filterStorageKey) ||
              (!this.ranking ? localStorage.getItem(STORAGE_KEY) : null) ||
              "{}"
          );
        } catch {
          /* Storage is optional. */
        }
        const query = this.$route.query;
        const code = String(
          query.institution ??
            (this.selection.initialized
              ? this.selection.institution
              : saved.institution) ??
            ""
        );
        this.institution = this.institutions.some((item) => item.code === code)
          ? code
          : "";
        this.program = String(
          query.program ??
            (query.institution != null
              ? ""
              : this.selection.initialized
              ? this.selection.program
              : saved.institution === code
              ? saved.program || ""
              : "")
        );
        const semester =
          query.semester ?? (saved.institution === code ? saved.semester : "");
        this.semester = ["1", "3"].includes(semester) ? semester : "";
        const sort =
          query.sort ??
          (saved.institution === code && saved.sort
            ? saved.sort
            : this.ranking
            ? "grade_desc"
            : "code");
        this.sort = [
          "grade_desc",
          "grade_asc",
          "fail_asc",
          "fail_desc",
        ].includes(sort)
          ? sort
          : "code";
        this.search = String(
          query.q ?? (saved.institution === code ? saved.search || "" : "")
        ).slice(0, 120);
        const migrateSelection = !this.selection.initialized;
        courseSelection.setContext({
          institution: this.institution,
          program: this.program,
        });
        if (
          migrateSelection &&
          saved.institution === this.institution &&
          Array.isArray(saved.courses)
        )
          this.shortlist = saved.courses
            .filter(
              (item) =>
                typeof item?.course_code === "string" &&
                typeof item?.course_name === "string"
            )
            .filter(
              (item, index, items) =>
                items.findIndex(
                  (other) => other.course_code === item.course_code
                ) === index
            )
            .slice(0, this.compareLimit)
            .map((item) => ({
              course_code: item.course_code,
              course_name: item.course_name,
            }));
        if (this.institution) {
          await this.loadPrograms();
          if (this.disposed) return;
          courseSelection.setContext({
            institution: this.institution,
            program: this.program,
          });
          await this.loadCourses();
        }
      } catch {
        this.error = this.ui(
          "Kunne ikke hente studiesteder.",
          "Unable to load institutions."
        );
      } finally {
        this.loadingInstitutions = false;
        this.contextReady = !this.disposed;
      }
    },
    async loadPrograms() {
      const version = ++this.programVersion;
      this.programs = [];
      this.loadingPrograms = true;
      this.programError = "";
      try {
        const response = await ApiService.getStudyPrograms(this.institution);
        if (version !== this.programVersion) return;
        this.programs = response.data;
        if (!this.programs.some((item) => item.code === this.program))
          this.program = "";
      } catch {
        if (version === this.programVersion)
          this.programError = this.ui(
            "Studieprogrammene kunne ikke hentes.",
            "Unable to load programmes."
          );
      } finally {
        if (version === this.programVersion) this.loadingPrograms = false;
      }
    },
    async changeInstitution() {
      clearTimeout(this.searchTimer);
      this.requestController?.abort();
      this.requestVersion++;
      this.program = "";
      this.programs = [];
      this.courses = [];
      this.payload = null;
      courseSelection.setContext({
        institution: this.institution,
        program: "",
      });
      this.page = 1;
      this.error = "";
      this.programError = "";
      this.loading = false;
      this.persist();
      this.updateQuery();
      if (!this.institution) {
        this.programVersion++;
        this.loadingPrograms = false;
        return;
      }
      const code = this.institution;
      const programs = this.loadPrograms();
      await this.loadCourses();
      await programs;
      if (code !== this.institution) return;
    },
    queueSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.changeFilters(), 300);
    },
    changeFilters() {
      clearTimeout(this.searchTimer);
      courseSelection.setContext({
        institution: this.institution,
        program: this.program,
      });
      this.page = 1;
      this.loadCourses();
    },
    resetSearch() {
      this.search = "";
      this.semester = "";
      this.changeFilters();
    },
    async goToPage(page) {
      if (this.loading) return;
      this.page = page;
      const loaded = await this.loadCourses();
      if (!loaded) return;
      await this.$nextTick();
      this.$refs.listContext?.focus({ preventScroll: true });
      this.$refs.listContext?.scrollIntoView({ block: "start" });
    },
    updateQuery() {
      const query = {
        ...(this.institution ? { institution: this.institution } : {}),
        ...(this.program ? { program: this.program } : {}),
        ...(this.semester ? { semester: this.semester } : {}),
        ...(this.search.trim() ? { q: this.search.trim() } : {}),
        ...(this.sort !== "code" ? { sort: this.sort } : {}),
      };
      this.$router.replace({ query });
    },
    async loadCourses() {
      if (!this.institution) return;
      const version = ++this.requestVersion;
      this.requestController?.abort();
      this.requestController = markRaw(new AbortController());
      this.loading = true;
      this.error = "";
      this.updateQuery();
      this.persist();
      try {
        const response = await ApiService.getCourseCatalog(
          {
            institution: this.institution,
            program: this.program || undefined,
            q: this.search.trim() || undefined,
            semester: this.semester || undefined,
            sort: this.sort,
            page: this.page,
          },
          this.requestController.signal
        );
        if (version !== this.requestVersion) return;
        this.payload = response.data;
        this.courses = response.data.courses;
        this.page = response.data.page;
        return true;
      } catch (error) {
        if (version !== this.requestVersion || error.code === "ERR_CANCELED")
          return;
        this.error = [402, 403].includes(error.response?.status)
          ? this.ui(
              "Dette programutvalget krever Premium. Velg «Alle studieprogram» for å utforske studiestedet.",
              "This programme selection requires Premium. Choose “All programmes” to explore the institution."
            )
          : this.ui(
              "Emnelisten kunne ikke hentes. Prøv igjen.",
              "Unable to load the course list. Please retry."
            );
        this.courses = [];
      } finally {
        if (version === this.requestVersion) this.loading = false;
      }
    },
    isSelected(course) {
      return this.shortlist.some(
        (item) => item.course_code === course.course_code
      );
    },
    toggleCourse(course) {
      if (this.isSelected(course))
        this.shortlist = this.shortlist.filter(
          (item) => item.course_code !== course.course_code
        );
      else if (this.shortlist.length < this.compareLimit && !course.data_issue)
        courseSelection.toggle(course, this.compareLimit);
      this.feedback = `${this.shortlist.length} ${this.ui(
        `${this.courseNoun(this.shortlist.length)} i kortlisten.`,
        `${this.courseNoun(this.shortlist.length)} in your shortlist.`
      )}`;
      this.persist();
    },
    persist() {
      try {
        localStorage.setItem(
          this.filterStorageKey,
          JSON.stringify({
            institution: this.institution,
            program: this.program,
            semester: this.semester,
            sort: this.sort,
            search: this.search,
          })
        );
      } catch {
        /* Storage is optional. */
      }
    },
    compareCourses() {
      if (this.shortlist.length < 2) return;
      this.$router.push({
        name: "home",
        query: {
          courses: this.shortlist.map((course) => course.course_code).join(","),
          layout: "combined",
          source: this.ranking ? "rankings" : "explore",
          institution: this.institution,
          ...(this.program ? { program: this.program } : {}),
        },
      });
    },
  },
};
</script>

<style scoped>
.catalog-page {
  max-width: 1140px;
  margin: auto;
  padding: 18px 20px 28px;
  text-align: left;
  color: #213f30;
}
.eyebrow {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.5px;
  color: #64795d;
  margin-bottom: 12px;
}
.catalog-heading h1 {
  font-size: clamp(29px, 4vw, 43px);
  letter-spacing: -1.8px;
  line-height: 1.15;
  font-weight: 500;
  margin: 0 0 15px;
}
.catalog-heading > p:last-child {
  color: #687667;
  font-size: 14px;
  line-height: 1.8;
  max-width: 650px;
}
.context-summary {
  display: none;
}
.program-code {
  display: none;
}
.ranking-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 18px 0;
}
.ranking-presets button {
  border: 1px solid #d5dfcb;
  border-radius: 7px;
  padding: 10px 13px;
  background: #fff;
  font-size: 13px;
  color: #47603c;
}
.ranking-presets button[aria-pressed="true"] {
  background: #2c563d;
  color: #fff;
  border-color: #2c563d;
}
.context-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  background: #edf1e6;
  border: 1px solid #dce5d3;
  border-radius: 11px;
  padding: 16px;
  margin: 18px 0 16px;
}
label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #60735a;
  font-size: 11px;
  min-width: 0;
}
select {
  appearance: auto;
  background: #fff;
  color: #294731;
  border: 1px solid #cfdac7;
  border-radius: 7px;
  min-height: 45px;
  padding: 9px 12px;
  font: inherit;
  font-size: 13px;
  width: 100%;
}
select:disabled {
  opacity: 0.6;
}
.catalog-tools {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}
.catalog-tools > label:not(.catalog-search) {
  width: 170px;
  flex-shrink: 0;
}
.catalog-search {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 9px;
  border: 1px solid #d8e0d0;
  border-radius: 8px;
  background: #fff;
  height: 45px;
  padding: 0 12px;
}
.catalog-search input {
  min-width: 0;
  width: 100%;
  height: 100%;
  font-size: 13px;
  color: #294731;
}
.catalog-search input::placeholder {
  color: #71806a;
  opacity: 1;
}
.catalog-search input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}
.catalog-search:focus-within {
  border-color: #397454;
  box-shadow: 0 0 0 3px #39745420;
}
.catalog-search input:focus-visible {
  outline: none;
}
.list-context {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 11px;
  color: #6d7d64;
  margin: 23px 0 12px;
  scroll-margin-top: 90px;
}
.list-context:focus-visible {
  outline: 2px solid #397454;
  outline-offset: 5px;
  border-radius: 3px;
}
.list-context strong {
  font-size: 12px;
  font-weight: 500;
  color: #3a5532;
}
.catalog-table-wrap {
  border: 1px solid #dce3d5;
  background: #fff;
  border-radius: 11px;
  overflow: clip;
}
.catalog-table {
  border-collapse: collapse;
  width: 100%;
  text-align: left;
  font-variant-numeric: tabular-nums;
}
.catalog-table th {
  font-weight: 500;
  font-size: 11px;
  color: #6b7b62;
  padding: 14px 12px;
  border-bottom: 1px solid #e0e7d9;
  white-space: nowrap;
}
.catalog-table td {
  padding: 14px 12px;
  border-bottom: 1px solid #e8ede2;
  vertical-align: middle;
  font-size: 13px;
}
.catalog-table tr:last-child td {
  border: 0;
}
.catalog-table th:first-child,
.catalog-table td:first-child {
  padding-left: 20px;
  width: 45%;
}
.catalog-table td:last-child {
  padding-right: 18px;
}
.catalog-table tr.is-selected {
  background: #eff5e5;
}
.course-code {
  display: block;
  color: #71806a;
  font-size: 11px;
  letter-spacing: 0.45px;
  margin-bottom: 4px;
}
.course-identity > a {
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  color: #294731;
  text-decoration: none;
}
.course-identity > a:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.course-basis {
  margin-top: 6px;
  font-size: 11px;
  color: #728168;
}
.course-basis summary {
  cursor: pointer;
  list-style: none;
  display: inline;
}
.course-basis summary::after {
  content: " ⓘ";
}
.course-basis summary::-webkit-details-marker {
  display: none;
}
.course-basis p {
  max-width: 440px;
  font-size: 11px;
  line-height: 1.8;
  margin-top: 9px;
}
.course-identity :deep(.sample-indicator) {
  margin-top: 8px;
  width: fit-content;
}
.numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.catalog-table th.numeric {
  text-align: right;
}
.grade-letter {
  font-size: 23px;
  line-height: 1.2;
  font-weight: 500;
  display: block;
}
.grade-number {
  display: block;
  font-size: 10px;
  color: #74836c;
  margin-top: 4px;
}
.missing-credit {
  color: #819075;
  font-size: 11px;
}
.add-shortlist {
  font-size: 12px;
  min-height: 36px;
  min-width: 84px;
  padding: 7px 10px;
  border: 1px solid #d5dfcb;
  border-radius: 7px;
  background: #fff;
  color: #3c5934;
  white-space: nowrap;
}
.add-shortlist[aria-pressed="true"] {
  background: #2c563d;
  color: #fff;
  border-color: #2c563d;
}
.add-shortlist:not(:disabled):hover {
  border-color: #397454;
  box-shadow: 0 0 0 2px #39745418;
}
button {
  cursor: pointer;
}
button:disabled {
  opacity: 0.45;
  cursor: default;
}
button:focus-visible,
a:focus-visible,
input:focus-visible,
select:focus-visible,
summary:focus-visible {
  outline: 3px solid #397454;
  outline-offset: 3px;
}
.catalog-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  font-size: 11px;
  color: #62765a;
}
.catalog-pagination button,
.empty-results button {
  border: 1px solid #d5dfcb;
  background: #fff;
  border-radius: 7px;
  padding: 9px 14px;
  font-size: 12px;
}
.catalog-method {
  margin: 22px 0;
  color: #687a5e;
  font-size: 11px;
  line-height: 1.9;
}
.catalog-method summary {
  cursor: pointer;
}
.catalog-method p {
  max-width: 800px;
  padding-top: 10px;
}
.shortlist-dock {
  position: sticky;
  bottom: 16px;
  z-index: 15;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 15px 25px;
  background: #243f2e;
  color: #fff;
  border-radius: 12px;
  padding: 20px 22px;
  margin: 25px 0;
  box-shadow: 0 10px 30px #213f3020;
}
.shortlist-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.shortlist-title h2 {
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.4px;
}
.shortlist-title > span {
  border: 1px solid #648066;
  border-radius: 4px;
  padding: 1px 7px;
  font-size: 10px;
  color: #d6e2cc;
}
.clear-shortlist {
  color: #c4d5b7;
  font-size: 11px;
  margin-left: auto;
  min-height: 32px;
  padding: 4px 6px;
  border-radius: 5px;
}
.shortlist-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 1;
  min-width: 0;
}
.shortlist-scroll-hint {
  display: none;
}
.shortlist-items:focus-visible {
  outline: 2px solid #d6ed91;
  outline-offset: 3px;
}
.shortlist-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid #557153;
  background: #2f4c37;
  border-radius: 7px;
  padding: 8px 10px 8px 12px;
  max-width: 100%;
  min-width: 0;
}
.shortlist-item > div {
  min-width: 0;
}
.shortlist-item span {
  display: block;
  font-size: 9px;
  color: #bccfb0;
  letter-spacing: 0.35px;
}
.shortlist-item strong {
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  display: block;
  overflow-wrap: anywhere;
  margin-top: 3px;
}
.shortlist-item button {
  font-size: 20px;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  color: #e4eddc;
  border-radius: 5px;
}
.shortlist-empty {
  color: #c4d5b7;
  font-size: 11px;
  line-height: 1.8;
  margin: 0;
}
.shortlist-actions {
  grid-column: 2;
  grid-row: 1/3;
  display: flex;
  flex-direction: column;
  gap: 7px;
  justify-content: center;
  text-align: center;
}
.compare-action {
  background: #d4ed87;
  color: #2b492f;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  min-height: 44px;
  padding: 10px 20px;
  white-space: nowrap;
}
.compare-action-compact {
  display: none;
}
.shortlist-actions > span {
  font-size: 10px;
  color: #c3d4b6;
}
.catalog-loading {
  text-align: center;
  padding: 70px 20px;
  color: #68805d;
  font-size: 13px;
}
.catalog-error {
  border: 1px solid #e8cfbc;
  background: #fff3eb;
  color: #8a5235;
  border-radius: 8px;
  padding: 16px;
  font-size: 12px;
  margin: 20px 0;
  line-height: 1.8;
}
.catalog-error button {
  text-decoration: underline;
  margin-left: 10px;
}
.catalog-welcome {
  padding: 48px 15px;
  text-align: center;
}
.catalog-welcome > span {
  display: inline-grid;
  place-items: center;
  background: #e5edd7;
  color: #527b43;
  border-radius: 12px;
  width: 50px;
  height: 50px;
  font-size: 27px;
  margin-bottom: 20px;
}
.catalog-welcome h2,
.empty-results h2 {
  font-size: 23px;
  font-weight: 500;
  letter-spacing: -0.6px;
}
.catalog-welcome p,
.empty-results p {
  color: #74846a;
  font-size: 12px;
  line-height: 1.9;
  max-width: 490px;
  margin: 12px auto 20px;
}
.catalog-welcome > div {
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}
.catalog-welcome button {
  border: 1px solid #d8e2cc;
  background: #fff;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 12px;
}
.empty-results {
  text-align: center;
  padding: 35px 20px;
}
.clear-search {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  font-size: 21px;
  color: #6a7d60;
  border-radius: 5px;
}
.clear-search:hover {
  background: #edf3e5;
}
.active-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 13px;
}
.active-filters button {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  background: #eaf0e1;
  color: #47603c;
  padding: 6px 9px;
  border-radius: 5px;
  font-size: 11px;
  line-height: 1.5;
}
.active-filters button > span:first-child {
  overflow-wrap: anywhere;
}
.active-filters .reset-filters {
  background: transparent;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.catalog-data-note {
  font-size: 11px;
  line-height: 1.6;
  color: #75856c;
  margin: -4px 0 10px;
}
.catalog-table-wrap.is-updating {
  opacity: 0.55;
  pointer-events: none;
}
.shortlist-disclosure {
  margin-left: auto;
  color: #d7e6cb;
  font-size: 11px;
  min-height: 30px;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border-radius: 5px;
}
.shortlist-dock button:focus-visible,
.shortlist-dock h2:focus-visible {
  outline-color: #d4ed87;
}
.shortlist-disclosure:hover,
.clear-shortlist:hover,
.shortlist-item button:hover {
  background: #ffffff15;
  color: #fff;
}
.compare-action:not(:disabled):hover {
  background: #e0f4a9;
}
.shortlist-title > .clear-shortlist {
  margin-left: 5px;
}
.shortlist-collapsed .shortlist-actions {
  grid-row: 1;
}
.shortlist-empty-state {
  position: static;
}
.catalog-source {
  border-top: 1px solid #dce4d4;
  padding-top: 20px;
  margin-top: 26px;
  font-size: 10px;
  color: #7b886f;
  line-height: 1.9;
}
.catalog-source a {
  color: #4b7040;
  margin-left: 8px;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (max-width: 760px) {
  .catalog-tools {
    flex-wrap: wrap;
  }
  .catalog-search {
    flex-basis: 100%;
  }
  .catalog-tools > label:not(.catalog-search) {
    flex: 1;
    width: auto;
  }
  .catalog-table td,
  .catalog-table th {
    padding-inline: 9px;
  }
  .catalog-table th:first-child,
  .catalog-table td:first-child {
    padding-left: 14px;
  }
  .catalog-table th:first-child {
    width: 38%;
  }
  .shortlist-dock {
    gap: 13px 18px;
    padding: 18px;
  }
}
@media (max-width: 600px) {
  .has-context .catalog-heading > p:last-child {
    display: none;
  }
  .program-filter-chip > span:first-child {
    display: none;
  }
  .program-filter-chip .program-code {
    display: inline;
  }
  .context-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px;
    margin: 12px 0;
    border: 1px solid #dce5d3;
    border-radius: 8px;
    background: #edf1e6;
    text-align: left;
    color: #294731;
  }
  .context-summary > span:first-child {
    display: grid;
    gap: 3px;
    min-width: 0;
  }
  .context-summary strong {
    font-size: 13px;
    font-weight: 500;
  }
  .context-summary > span:first-child > span {
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .context-summary > span:last-child {
    font-size: 12px;
    flex-shrink: 0;
  }
  .context-filters.context-collapsed {
    display: none;
  }
  .catalog-table.without-credits tr {
    grid-template-columns: 1.2fr 0.8fr 0.8fr;
  }
  .catalog-table.without-credits td:first-child,
  .catalog-table.without-credits td:last-child {
    grid-column: 1 / -1;
  }
  .ranking-presets {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 12px 0 14px;
  }
  .catalog-tools.ranking-tools {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 105px;
    gap: 10px;
  }
  .catalog-tools.ranking-tools > label:not(.catalog-search) {
    width: auto;
  }
  .list-context {
    margin-top: 18px;
  }
  .catalog-page {
    padding: 20px 4px 20px;
  }
  .context-filters {
    grid-template-columns: 1fr;
    padding: 16px;
    gap: 16px;
  }
  .catalog-heading h1 {
    font-size: 31px;
  }
  .catalog-heading > p:last-child {
    font-size: 13px;
  }
  .catalog-tools select,
  .catalog-search input,
  .context-filters select {
    font-size: 16px;
  }
  .catalog-tools > label {
    min-width: 0;
  }
  .catalog-table,
  .catalog-table tbody {
    display: block;
  }
  .catalog-table thead {
    display: none;
  }
  .catalog-table tr {
    display: grid;
    grid-template-columns: 1.35fr 1fr 0.65fr 0.8fr;
    gap: 15px 8px;
    padding: 17px 15px;
    border-bottom: 1px solid #e3eada;
  }
  .catalog-table tr:last-child {
    border: 0;
  }
  .catalog-table td {
    padding: 0 !important;
    border: 0 !important;
    display: block;
    font-size: 12px;
  }
  .catalog-table td:first-child {
    grid-column: 1/5;
    width: auto;
  }
  .catalog-table td:last-child {
    grid-column: 1/5;
  }
  .catalog-table td:not(:first-child):not(:last-child)::before {
    content: attr(data-label);
    font-size: 10px;
    color: #7b896e;
    display: block;
    margin-bottom: 5px;
  }
  .catalog-table .numeric {
    text-align: left;
    white-space: normal;
  }
  .grade-letter {
    font-size: 20px;
  }
  .grade-number {
    font-size: 10px;
  }
  .add-shortlist {
    width: 100%;
    min-height: 44px;
  }
  .missing-credit {
    font-size: 11px;
  }
  .shortlist-dock {
    grid-template-columns: 1fr;
    bottom: max(8px, env(safe-area-inset-bottom, 0px));
    padding: 16px;
    gap: 12px;
  }
  .shortlist-collapsed .shortlist-actions {
    grid-row: 2;
  }
  .shortlist-collapsed.shortlist-ready .shortlist-actions > span {
    display: none;
  }
  .shortlist-collapsed {
    gap: 9px;
  }
  .shortlist-title {
    gap: 7px;
  }
  .shortlist-title h2 {
    font-size: 14px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .shortlist-title > span {
    white-space: nowrap;
    flex-shrink: 0;
  }
  .shortlist-disclosure {
    white-space: nowrap;
    font-size: 10px;
    padding: 0;
    min-height: 32px;
  }
  .shortlist-actions {
    grid-column: 1;
    grid-row: auto;
  }
  .shortlist-items {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 3px;
    scroll-snap-type: x proximity;
    scroll-padding-inline: 2px;
  }
  .shortlist-scroll-hint {
    display: block;
    margin: 0;
    font-size: 10px;
    line-height: 1.5;
    color: #d6e2cc;
  }
  .shortlist-item {
    flex: 0 0 190px;
    scroll-snap-align: start;
  }
  .shortlist-item button {
    width: 32px;
    height: 32px;
  }
  .shortlist-item strong {
    font-size: 11px;
  }
  .catalog-method {
    font-size: 11px;
  }
  .shortlist-collapsed .clear-shortlist {
    display: none;
  }
  .shortlist-collapsed:not(.shortlist-empty-state) {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
  }
  .shortlist-collapsed:not(.shortlist-empty-state) .shortlist-title {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0 5px;
  }
  .shortlist-collapsed .shortlist-title h2 {
    font-size: 12px;
  }
  .shortlist-collapsed .shortlist-title > span {
    padding-inline: 5px;
  }
  .shortlist-collapsed .shortlist-disclosure {
    grid-column: 1 / -1;
    justify-self: start;
    margin-left: 0;
  }
  .shortlist-collapsed:not(.shortlist-empty-state) .shortlist-actions {
    grid-column: 2;
    grid-row: 1;
  }
  .shortlist-collapsed:not(.shortlist-empty-state) .compare-action {
    padding-inline: 12px;
  }
  .shortlist-collapsed:not(.shortlist-empty-state) .compare-action-full {
    display: none;
  }
  .shortlist-collapsed:not(.shortlist-empty-state) .compare-action-compact {
    display: inline;
  }
}
</style>
